import { useState, useEffect, RefObject } from "react";

/**
 * Custom hook that detects when an element is visible in the viewport
 * @param ref - Reference to the element to observe
 * @param threshold - Percentage of element that needs to be visible (0-1)
 * @param rootMargin - Margin around the root
 * @returns boolean indicating if the element is visible
 */
export function useOnScreen(
  ref: RefObject<HTMLElement>,
  threshold: number = 0.1,
  rootMargin: string = "0px"
): boolean {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
        threshold,
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, rootMargin, threshold]);

  return isIntersecting;
}
